/**
 * Single source of truth for the design system's overlay motion.
 *
 * The class names and the durations have to agree: `usePresence` defers an
 * unmount by a number of milliseconds, and the CSS animation takes a number of
 * milliseconds, and if they drift the overlay either vanishes mid-animation or
 * lingers after it. `__tests__/motion.test.tsx` asserts every `exitMs` here equals
 * the duration `tailwind.config.js` declares for the matching animation, so the
 * drift cannot happen quietly.
 *
 * Entrances carry no fill-mode (they end at the element's natural state); exits
 * carry `forwards` (they do not) — see `okf/design-system/e2e.md` § 12.
 */
export const MOTION = {
  overlay: {
    enter: 'motion-safe:tw-animate-overlay-fade',
    exit: 'motion-safe:tw-animate-overlay-fade-out',
    exitMs: 160,
    /**
     * The scrim itself, shared by Dialog and SidePanel so the two cannot drift
     * apart — the showcase renders them one after the other, and two different
     * scrims behind the same page reads as a bug.
     *
     * `tw-bg-scrim` resolves `--ds-scrim` (§ 205) — the ink at 50% in light (grey-900
     * when § 205 introduced it; § 253 re-based it) and the dark page ground at 70% in
     * dark (§§ 218, 253; § 414 corrected this comment), alpha baked
     * into the token because a var()-backed colour cannot compose with
     * `tw-bg-opacity-*`. This was the design system's LAST live palette step
     * (`tw-bg-grey-900 tw-bg-opacity-50`, same rendered value); now the scrim
     * rides the theming seam like every other colour. Navy rather than black:
     * the system's ink is navy, and a neutral-black scrim over it goes muddy
     * brown. The `backdrop-blur` is what actually separates the layers, which is
     * why the scrim can stay at 50% — pushing it darker to get the same
     * separation costs legibility of the page behind on every single open.
     */
    scrim: 'tw-bg-scrim tw-backdrop-blur-sm',
  },
  dialog: {
    enter: 'motion-safe:tw-animate-dialog-pop',
    exit: 'motion-safe:tw-animate-dialog-pop-out',
    exitMs: 140,
  },
  /**
   * The tab indicator is the one piece of motion here that is a **transition**, not a
   * keyframe animation — so its duration lives in the utility class, not in
   * `tailwind.config.js`, and `motion.test.tsx` checks the two agree by reading the
   * `duration-*` class rather than the animation table.
   *
   * `transition-transform`, deliberately, not `transition-[transform,width]`:
   *
   * - A comma inside an arbitrary value **does not compile when a variant is
   *   present**. `tw-transition-[transform,width]` is fine bare and
   *   `motion-safe:tw-transition-[transform]` is fine, but
   *   `motion-safe:tw-transition-[transform,width]` emits nothing at all — silently,
   *   as always. Checked against the compiler before being written here.
   * - Transform-only is also the better animation: translate and scale run on the
   *   compositor, where animating `left`/`width` would lay out and paint the bar on
   *   every frame.
   *
   * The indicator is therefore a 1px-wide bar driven by
   * `translateX(left) scaleX(width)` with a left origin — `scaleX` of a 1px base is
   * the width in pixels. That is also why it carries no `rounded-t-sm` any more: a 2px
   * radius on a bar scaled 80x becomes a 160px horizontal curve, which reads as a
   * shallow lens rather than a rounded end.
   */
  tab: {
    indicator:
      /* `ease-swift` (§ 219) — the shared settle curve; stock `ease-out` stops
         decelerating so early that the bar's last 40px read as a slow crawl. */
      'motion-safe:tw-transition-transform motion-safe:tw-duration-200 motion-safe:tw-ease-swift',
    ms: 200,
  },
  /**
   * § 242 — the anchored popovers: Dropdown's listbox, DropdownButton's menu,
   * Combobox's list. Each owns its open state, so each can hold the surface for
   * `exitMs` through `usePresence` and run the reverse of `popup-rise`. While it
   * exits the surface is `aria-hidden` and `pointer-events-none` — gone from the
   * accessibility tree and from the hit-test the instant it closes, visible for
   * one more frame. `Menu` is NOT here: its mounting is caller-owned (~20 sites
   * render it conditionally), so it cannot hold itself for an exit. `Tooltip`
   * stays entrance-only by design (§ 219 — an exit chases a pointer that left).
   */
  popover: {
    enter: 'motion-safe:tw-animate-popup-rise',
    exit: 'motion-safe:tw-animate-popup-rise-out',
    exitMs: 100,
  },
  panel: {
    enter: 'motion-safe:tw-animate-panel-slide',
    exit: 'motion-safe:tw-animate-panel-slide-out',
    exitMs: 200,
  },
  /**
   * Toasts. Not routed through `exitDurationFor` — a toast has no scrim to wait for,
   * so the surface duration IS the wait. `Toast` calls `usePresence` per item with
   * this `exitMs`, which is also what makes reduced motion correct for free:
   * `usePresence` unmounts immediately when the animation will not run, so a toast
   * cannot get stuck on screen waiting for an `animationend` that never fires.
   */
  toast: {
    enter: 'motion-safe:tw-animate-toast-rise',
    exit: 'motion-safe:tw-animate-toast-rise-out',
    exitMs: 140,
  },
} as const;

/**
 * The overlay and its surface animate for different lengths, so the wrapper has to
 * stay mounted for the longer of the two or the slower one is cut off.
 */
export const exitDurationFor = (surface: 'dialog' | 'panel'): number =>
  Math.max(MOTION.overlay.exitMs, MOTION[surface].exitMs);
